import { useQueryClient } from '@tanstack/react-query';
import useKlydoMutation, {
  UseKlydoMutationOptions,
} from '../../../hooks/useKlydoMutation';
import collectionApiClient, {
  PopulatedDraftCollection,
} from '../../../api/collections';

const useRemoveCollectionMutation = (
  options?: UseKlydoMutationOptions<
    void,
    string,
    { previousCollections: Map<string, PopulatedDraftCollection[]> }
  >,
) => {
  const queryClient = useQueryClient();

  return useKlydoMutation<
    void,
    string,
    { previousCollections: Map<string, PopulatedDraftCollection[]> }
  >({
    mutationFn: (collectionId: string) =>
      collectionApiClient.deleteCollection(collectionId),
    mutationKey: ['collections'],
    onMutate: async (collectionId: string) => {
      await queryClient.cancelQueries({ queryKey: ['collections'] });

      const previousCollections = new Map<string, PopulatedDraftCollection[]>();

      queryClient
        .getQueriesData({ queryKey: ['collections'] })
        .forEach(([queryKey, data]) => {
          if (Array.isArray(data)) {
            const collections = data as PopulatedDraftCollection[];
            previousCollections.set(JSON.stringify(queryKey), collections);

            const updatedCollections = collections.filter(
              (collection) => collection.id !== collectionId,
            );
            queryClient.setQueryData(queryKey, updatedCollections);
          }
        });

      return { previousCollections };
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
    ...options,
  });
};

export default useRemoveCollectionMutation;
